<template>
  <BaseFetchData :action="loadComponentData">
    <template #default="fetchDataProps">
      <div v-if="fetchDataProps.status.loading">
        <Loader />
      </div>
      <div v-else-if="fetchDataProps.status.resolved">
        <div class="bg-white shadow sm:rounded-lg overflow-hidden">
          <div class="flex divide-x">
            <div class="w-64">
              <TagCategoryNavigation
                :account-id="accountId"
                :tag-categories="allTagCategories"
                :current-tag-category="currentTagCategory"
                @switch="switchTagCategory"
                @tag-category-created="onTagCategoryCreated"
                @tag-category-imported="onTagCategoryImported"
              />
            </div>
            <div class="flex-1 divide-y">
              <CurrentTagCategory
                ref="tagCategory"
                v-if="currentTagCategory"
                :key="currentTagCategory.id"
                :tag-category="currentTagCategory"
                @tag-category-updated="onTagCategoryUpdated"
                @tag-category-deleted="onTagCategoryDeleted"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="h-full flex items-center justify-center">
        <div class="space-y-4 text-center">
          <p class="mx-auto">
            {{ fetchDataProps.rejectReason }}
          </p>
        </div>
      </div>
    </template>
  </BaseFetchData>
</template>

<script>
import { omit } from 'lodash-es';
import { accountTagCategoriesClient } from '@/api/clients';
import mapKeysToCamelCase from '@/utils/map-keys-to-camel-case';
import TagCategoryNavigation from './TagCategoryNavigation.vue';
import CurrentTagCategory from './CurrentTagCategory.vue';

export default {
  components: {
    TagCategoryNavigation,
    CurrentTagCategory,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      tags: {
        byId: {},
        idsByTagCategoryId: {},
      },
      tagCategories: {
        byId: {},
        ids: [],
      },
      currentTagCategoryId: null,
    };
  },
  computed: {
    currentTagCategory() {
      return this.tagCategories.byId[this.currentTagCategoryId] || null;
    },
    currentTagCategoryIndex() {
      return this.tagCategories.ids.indexOf(this.currentTagCategoryId);
    },
    previousTagCategoryIndex() {
      if (this.currentTagCategoryIndex <= 0) {
        return -1;
      }

      return this.currentTagCategoryIndex - 1;
    },
    nextTagCategoryIndex() {
      if (this.currentTagCategoryIndex === this.tagCategories.ids.length - 1) {
        return -1;
      }

      return this.currentTagCategoryIndex + 1;
    },
    allTagCategories() {
      return this.tagCategories.ids.map((id) => this.tagCategories.byId[id]).filter(Boolean);
    },
  },
  methods: {
    async loadComponentData() {
      try {
        await this.loadTagCategories();

        const [defaultTagCategoryId = null] = this.tagCategories.ids;

        this.currentTagCategoryId = defaultTagCategoryId;
      } catch (error) {
        // handle error
      }
    },
    async loadTagCategories() {
      try {
        const response = await accountTagCategoriesClient.list(this.accountId);

        response.data.forEach(this.setTagCategory);

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    setTagCategory(tagCategory) {
      this.tagCategories.byId = {
        ...this.tagCategories.byId,
        [tagCategory.id]: mapKeysToCamelCase(tagCategory),
      };

      if (this.tagCategories.ids.indexOf(tagCategory.id) >= 0) {
        return;
      }

      this.tagCategories.ids.push(tagCategory.id);
    },
    removeTagCategory(tagCategory) {
      const tagCategoryIdsToKeep = this.tagCategories.ids.filter((id) => id !== tagCategory.id);
      this.tagCategories.ids = [...tagCategoryIdsToKeep];

      this.tagCategories.byId = { ...omit(this.tagCategories.byId, [tagCategory.id]) };
    },
    switchTagCategory(tagCategory) {
      if (!tagCategory) {
        return;
      }

      this.currentTagCategoryId = tagCategory.id;
    },
    onTagCategoryCreated(newTagCategory) {
      this.setTagCategory(newTagCategory);
      this.switchTagCategory(newTagCategory);
    },
    onTagCategoryImported(newTagCategory) {
      this.setTagCategory(newTagCategory);

      if (this.currentTagCategoryId === newTagCategory.id) {
        this.$refs.tagCategory.refreshList();
        return;
      }

      this.switchTagCategory(newTagCategory);
    },
    onTagCategoryUpdated(updatedTagCategory) {
      this.setTagCategory(updatedTagCategory);
    },
    onTagCategoryDeleted(deletedTagCategory) {
      this.removeTagCategory(deletedTagCategory);
      this.switchTagCategory(this.allTagCategories?.[0]);
    },
  },
};
</script>
